
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { modelService } from '@/services/ai/modelService';
import DialogInfo from './DialogInfo';
import SummaryPoints from './SummaryPoints';
import { Loader2, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';

interface AISummaryProps {
  aiSummary: string | string[];
  aiResponse: string;
  summaryStatus?: 'pending' | 'generating' | 'completed' | 'error';
  summaryError?: string | null;
  context?: string;
  question?: string;
  answer?: string;
  intervieweeName?: string;
  title?: string;
  company?: string;
  interviewId?: string;
}

const AISummary = ({ 
  aiSummary = '', 
  aiResponse = '',
  summaryStatus = 'completed',
  summaryError,
  context = '',
  question = '',
  answer = '',
  intervieweeName = '',
  title = '',
  company = '',
  interviewId = ''
}: AISummaryProps) => {
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const { t } = useTranslation();

  // Processing AI summary data

  // Ensure aiSummary is always in a consistent format
  const processedSummary = React.useMemo(() => {
    if (!aiSummary) return [];
    
    if (typeof aiSummary === 'string') {
      if (aiSummary.trim().length === 0) return [];
      
      // Try to parse JSON string if applicable
      try {
        const parsed = JSON.parse(aiSummary);
        if (Array.isArray(parsed)) {
          return parsed.filter(item => !!item && item.trim().length > 0);
        }
      } catch (e) {
        // Not JSON, continue with normal processing
      }
      
      // If string contains line breaks or bullet points (but NOT hyphens in words), split it
      if (aiSummary.includes('\n') || aiSummary.includes('•')) {
        return aiSummary
          .split(/\n|•/)
          .map(point => point.trim())
          .filter(Boolean);
      }
      
      // Otherwise, treat as a single point
      return [aiSummary.trim()];
    }
    
    if (Array.isArray(aiSummary)) {
      // Filter out any null/undefined values and ensure all items are strings
      return aiSummary
        .filter(point => point !== null && point !== undefined)
        .map(point => String(point))
        .filter(point => point.trim().length > 0);
    }
    
    return [];
  }, [aiSummary]);

  const handleRegenerateSummary = async () => {
    if (!answer || isRegenerating || !interviewId) return;
    
    try {
      setIsRegenerating(true);
      toast.info('Regenererer oppsummering...');
      
      // Use the modelService to regenerate the summary
      await modelService.generateInterviewSummary(interviewId);
      
      toast.success('Oppsummering blir generert på nytt. Dette kan ta litt tid.');
    } catch (error) {
      console.error('Error regenerating summary:', error);
      toast.error('Kunne ikke regenerere oppsummering. Vennligst prøv igjen.');
    } finally {
      setIsRegenerating(false);
    }
  };

  if (summaryStatus === 'generating') {
    return (
      <div className="mt-8 bg-gray-50 p-4 md:p-6 rounded-lg">
        <div className="flex items-center justify-center space-x-2">
          <Loader2 className="w-6 h-6 animate-spin text-[#ea384c]" />
          <p>Genererer oppsummering...</p>
        </div>
      </div>
    );
  }

  if (summaryStatus === 'error') {
    return (
      <div className="mt-8 bg-red-50 p-4 md:p-6 rounded-lg">
        <div className="flex flex-col space-y-4">
          <p className="text-red-600">Kunne ikke generere oppsummering: {summaryError}</p>
          <Button 
            variant="outline" 
            onClick={handleRegenerateSummary}
            disabled={isRegenerating}
            className="self-start flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${isRegenerating ? 'animate-spin' : ''}`} />
            Prøv igjen
          </Button>
        </div>
      </div>
    );
  }

  // Check if we have any summary content to display
  const hasSummaryContent = processedSummary.length > 0 || aiResponse;

  if (summaryStatus === 'pending' || !hasSummaryContent) {
    return (
      <div className="mt-8 bg-gray-50 p-4 md:p-6 rounded-lg">
        <div className="flex flex-col space-y-4">
          <p className="text-gray-500">{t('ai.noSummaryAvailable')}</p>
          <Button 
            variant="outline" 
            onClick={handleRegenerateSummary}
            disabled={isRegenerating}
            className="self-start flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${isRegenerating ? 'animate-spin' : ''}`} />
            {t('ai.generateSummary')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mt-8 bg-gray-50 p-4 md:p-6 rounded-lg">
        <SummaryPoints 
          points={processedSummary} 
          onInfoClick={() => setShowInfoDialog(true)} 
        />

        {aiResponse && (
          <p className="italic text-gray-600 mb-6">{aiResponse}</p>
        )}

      </div>

      <DialogInfo 
        open={showInfoDialog} 
        onOpenChange={setShowInfoDialog} 
      />
    </>
  );
};

export default AISummary;
